import uuidv4 from 'uuid/v4';

import { ChatMessageHistoryDataAction } from '../../Actions/ChatHistory';
import { MessageAction } from '../../Actions/MessageActions';
import { RecentChatUpdateAction } from '../../Actions/RecentChatActions';
import SDK from '../../Components/SDK';
import { handleTempArchivedChats, resolveDisappearAt } from '../../Helpers/Chat/ChatHelper';
import { CHAT_TYPE_GROUP } from '../../Helpers/Chat/Constant';
import { scheduleMessageExpiry } from '../../Helpers/Chat/DisappearScheduler';
import { formatDisappearAtMs, shiftDisappearAt } from '../../Helpers/Chat/disappearTime';
import { formatUserIdToJid } from '../../Helpers/Chat/User';
import logger from '../../Helpers/logger';
import { getMessageObjSender, getRecentChatMsgObj } from '../../Helpers/Utility';
import { pollsReceived } from '../../slice/pollSlice';
import { clearComposerSchedule, fetchScheduledCount } from '../../slice/scheduledMessagesSlice';
import { enqueueOutboxMessages, OUTBOX_KIND } from '../outbox/outboxQueue';
import { resetPollEvents } from './pollEvents';
import { POLL_BATCH_MAX, POLL_ERROR, POLL_MESSAGE_TYPE } from './pollRules';

// Polls drawn in the same frame are fetched in one batch call.
const BATCH_DELAY_MS = 30;
const queuedPollIds = new Set();
const loadingPollIds = new Set();
let batchTimer = null;

// Used by every change; a refusal meaning the copy is stale reloads the poll.
const runPollChange = (pollId, call) => async dispatch => {
    let response;
    try {
        response = await call();
    } catch (error) {
        logger.error('Poll change threw', { pollId, error });
        return { statusCode: 500, message: '' };
    }
    if (response?.statusCode === 200) {
        dispatch(pollsReceived([response.data]));
    } else if ([POLL_ERROR.POLL_CLOSED, POLL_ERROR.OPTION_LIMIT_REACHED].includes(response?.message)) {
        dispatch(refreshPoll(pollId));
    }
    return response || { statusCode: 500, message: '' };
};

/**
 * Creates a poll in a group. The poll message shows at once as Pending and goes
 * through the outbox, so it waits out a lost connection; the server sends the message.
 *
 * @param {object} params
 * @param {string} params.chatId Group id.
 * @param {object} params.userProfile Local user's vCard, as the composer holds it.
 * @param {string} params.question Question as typed.
 * @param {string[]} params.options Option texts in their order.
 * @param {boolean} params.multipleAnswers Whether a voter may pick several options.
 * @param {number|null} params.closesAt Deadline in epoch milliseconds, or null.
 * @param {string} params.receiptStatus Seen receipt setting, '0' or '1'.
 */
export const sendPoll =
    ({ chatId, userProfile, question, options, multipleAnswers, closesAt, receiptStatus }) =>
    async dispatch => {
        const jid = formatUserIdToJid(chatId, CHAT_TYPE_GROUP);
        const msgId = uuidv4();
        const disappearAt = resolveDisappearAt(CHAT_TYPE_GROUP, chatId);
        const snapshot = {
            pollId: '',
            question,
            options: options.map(text => ({ text })),
            multipleAnswers,
            closesAt,
            createdBy: userProfile?.data?.fromUser || '',
        };
        const senderParams = {
            jid,
            userProfile,
            chatType: CHAT_TYPE_GROUP,
            msgId,
            msgType: 'text',
            mentionedUsersIds: [],
        };
        const message = withPollBody(await getMessageObjSender(senderParams), snapshot);
        const localMessage = { ...message, ...(disappearAt && { disappear_at: disappearAt }) };

        dispatch(MessageAction(localMessage));
        dispatch(ChatMessageHistoryDataAction({ data: [localMessage], groupJid: jid }));
        if (disappearAt) scheduleMessageExpiry(localMessage, chatId);
        // A fresh meta, or the row would keep the previous message's poll activity.
        dispatch(
            RecentChatUpdateAction({
                ...withPollBody(getRecentChatMsgObj(senderParams), snapshot),
                msgType: 'poll',
                metaData: {},
            }),
        );

        SDK.sendTypingGoneStatus(jid);
        handleTempArchivedChats(jid, CHAT_TYPE_GROUP);
        dispatch(
            enqueueOutboxMessages([
                {
                    msgId,
                    kind: OUTBOX_KIND.POLL,
                    params: {
                        toJid: jid,
                        messageId: msgId,
                        question,
                        options,
                        multipleAnswers,
                        closesAt,
                        disappearAt: disappearAt || '',
                        receiptStatus,
                    },
                },
            ]),
        );
    };

/**
 * Creates a poll the server sends at the time picked in the composer's schedule menu
 * (FR193). Nothing is drawn in the thread; the chat's scheduled count is refreshed.
 *
 * @param {object} params Same fields as sendPoll, plus scheduledAt (epoch milliseconds).
 * @returns {Promise<object>} The SDK response; refusals keep their errorCode (E37, E55).
 */
export const schedulePoll =
    ({ chatId, question, options, multipleAnswers, closesAt, receiptStatus, scheduledAt }) =>
    async dispatch => {
        const toJid = formatUserIdToJid(chatId, CHAT_TYPE_GROUP);
        const disappearAt = shiftDisappearAt(resolveDisappearAt(CHAT_TYPE_GROUP, chatId), scheduledAt);
        const params = { toJid, question, options, multipleAnswers, closesAt, receiptStatus, scheduledAt };
        let response;
        try {
            response = await SDK.createPoll({
                ...params,
                disappearAt: disappearAt ? formatDisappearAtMs(disappearAt) : '',
            });
        } catch (error) {
            logger.error('Scheduled poll threw', { chatId, error });
            response = { statusCode: 500 };
        }
        if (response?.statusCode === 200) {
            dispatch(clearComposerSchedule(chatId));
            dispatch(fetchScheduledCount(toJid));
        }
        return response;
    };

/**
 * Asks for a poll; requests made in the same frame share one batch call.
 *
 * @param {string} pollId Poll to load.
 */
export const requestPoll = pollId => dispatch => {
    if (!pollId || loadingPollIds.has(pollId)) return;
    queuedPollIds.add(pollId);
    if (batchTimer) return;
    batchTimer = setTimeout(() => {
        batchTimer = null;
        const pollIds = [...queuedPollIds];
        queuedPollIds.clear();
        dispatch(loadPolls(pollIds));
    }, BATCH_DELAY_MS);
};

/**
 * Loads one poll right away and keeps the answer.
 *
 * @param {string} pollId Poll to load.
 * @returns {Promise<object|null>} The poll, or null when it could not be read.
 */
export const refreshPoll = pollId => async dispatch => {
    const response = await SDK.getPoll(pollId);
    if (response?.statusCode !== 200) {
        logger.warn('Poll not loaded', { pollId, message: response?.message });
        return null;
    }
    dispatch(pollsReceived([response.data]));
    return response.data;
};

/**
 * Replaces the caller's vote. An empty selection is refused by the server.
 *
 * @param {string} pollId Poll voted on.
 * @param {string[]} optionIds New selection.
 * @returns {Promise<object>} SDK response.
 */
export const votePoll = (pollId, optionIds) => dispatch =>
    dispatch(runPollChange(pollId, () => SDK.setPollVote(pollId, optionIds)));

/**
 * Adds an option to an open poll; creator only.
 *
 * @param {object} poll Poll view model.
 * @param {string} text Option text as typed.
 * @returns {Promise<object>} SDK response.
 */
export const addOptionToPoll = (poll, text) => dispatch =>
    dispatch(runPollChange(poll.pollId, () => SDK.addPollOption(poll.pollId, poll.messageId, text)));

/**
 * Closes a poll for good; creator only.
 *
 * @param {string} pollId Poll to close.
 * @returns {Promise<object>} SDK response.
 */
export const closePollNow = pollId => dispatch => dispatch(runPollChange(pollId, () => SDK.closePoll(pollId)));

/**
 * Posts the poll again as the latest message of the chat.
 *
 * @param {string} pollId Poll to reshare.
 * @returns {Promise<object>} SDK response.
 */
export const resharePollNow = pollId => dispatch => dispatch(runPollChange(pollId, () => SDK.resharePoll(pollId)));

/**
 * Drops the poll work of the session that lives outside the store, e.g. on logout.
 */
export const resetPollState = () => {
    clearTimeout(batchTimer);
    batchTimer = null;
    queuedPollIds.clear();
    loadingPollIds.clear();
    resetPollEvents();
};

const loadPolls = pollIds => async dispatch => {
    for (let start = 0; start < pollIds.length; start += POLL_BATCH_MAX) {
        const chunk = pollIds.slice(start, start + POLL_BATCH_MAX);
        chunk.forEach(pollId => loadingPollIds.add(pollId));
        try {
            const response = await SDK.getPolls(chunk);
            if (response?.statusCode === 200) {
                dispatch(pollsReceived(response.data || []));
            } else {
                logger.warn('Polls not loaded', { count: chunk.length, message: response?.message });
            }
        } catch (error) {
            logger.error('Polls load threw', { count: chunk.length, error });
        } finally {
            chunk.forEach(pollId => loadingPollIds.delete(pollId));
        }
    }
};

const withPollBody = (message, snapshot) => ({
    ...message,
    msgBody: { ...message.msgBody, message_type: POLL_MESSAGE_TYPE, message: '', poll: snapshot },
});
